import type { GraphQLOptions, GraphQLResult } from "@aws-amplify/api-graphql"
import { API } from "src/shared/lib/amplify"
import clsx from "clsx"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import {
  LeftPanel,
  RadioGroup,
  RightPanel,
  SearchInput,
  SecondaryNav
} from "src/blitz"
import { sortByAlphanumericSpecialChars } from "src/blitz/DataGridTableNewDesign/helper"
import { TranslateComponent, useBulkTanslate } from "src/common/translations"
import { getPropertyMappingByEquipmentID } from "src/components/equipmentSummary/graphql"
import { useActiveDeviceIds } from "src/hooks/useActiveDeviceIds"
import { selectBuildingSummary } from "src/redux/slicers/buildingSummarySlice"
import { selectEquipmentSummary } from "src/redux/slicers/equipmentSummarySlice"
import {
  getEquipmentLoader,
  getEquipments,
  getFilter,
  getSelectedEquipments,
  setAHUCharacteristics,
  setSelectedAHUProperties,
  setSelectedEquipments
} from "src/redux/slicers/reportSlice"
import { selectLanguage } from "src/redux/slicers/userData"
import { filterEquipmentByDeviceIds } from "src/utils/analyticsHelper"
import { SearchRegex } from "src/utils/CommonMethods"
import { v4 as uuidv4 } from "uuid"
import { CURRENT_FETCH } from "../../Charts/constant"
import styles from "../ChillerReport/ChillerPerformanceReport.module.scss"

type Equipment = {
  id: string
  name: string
}

type AHUPropertyMappingItem = {
  targetKey: string
  value: string
}

type AHUPropertyMappingResponse = {
  getPropertyMappingByEquipmentID?: {
    items?: AHUPropertyMappingItem[]
  }
}

type FetchAHUPropertiesResponse = {
  characteristics: AHUPropertyMappingItem[]
  newFetch: string
}

const AHUEquipmentList = () => {
  const dispatch = useDispatch()
  const bulkTranslate = useBulkTanslate()
  const params = useParams()
  const { data: buildingSummary } = useSelector(selectBuildingSummary)
  const { buildingId } = buildingSummary?.buildingId ? buildingSummary : params
  const [searchText, setSearchText] = useState("")
  const equipLoading = useSelector(getEquipmentLoader)
  const allEquipments: Equipment[] = useSelector(getEquipments) || []
  const { activeDeviceIds, loadingDevices, devicesFetched } =
    useActiveDeviceIds(buildingId)

  const selectedItem: Equipment | null = useSelector(getSelectedEquipments)
  const { data: selectedEquipment } = useSelector(selectEquipmentSummary)
  const { equipmentType: selectedEquipmentType } = useSelector(getFilter)
  const selectedLanguage = useSelector(selectLanguage)
  const [translatedNamesById, setTranslatedNamesById] = useState<
    Record<string, string>
  >({})
  const handleSearch = (value: string) => {
    setSearchText(value)
  }
  const handleEquipmentChange = (ev: string) => {
    const selectedItem = equipments.find((item) => item?.id === ev) || {}
    dispatch(setSelectedEquipments(selectedItem))
  }

  const fetchAHUProperties = useCallback(
    async (
      equipmentId: string
    ): Promise<FetchAHUPropertiesResponse | undefined> => {
      try {
        const newFetch = uuidv4()
        dispatch(
          setAHUCharacteristics({
            type: "Loading",
            [CURRENT_FETCH]: newFetch
          })
        )
        const graphQLParams: GraphQLOptions = {
          authMode: "userPool",
          query: getPropertyMappingByEquipmentID,
          variables: { id: equipmentId }
        }
        const apiData = await API.graphql<
          GraphQLResult<AHUPropertyMappingResponse>
        >(graphQLParams)
        if ("subscribe" in apiData) return

        if (!apiData.errors) {
          const characteristics: AHUPropertyMappingItem[] =
            apiData?.data?.getPropertyMappingByEquipmentID?.items || []
          return { characteristics, newFetch }
        }
      } catch {
        dispatch(setAHUCharacteristics({ type: "Failure" }))
      }
    },
    [dispatch]
  )

  useEffect(() => {
    if (selectedItem?.id) {
      dispatch(setSelectedAHUProperties([]))
      const fetchCharacteristics = async () => {
        const response = await fetchAHUProperties(selectedItem?.id)
        dispatch(
          setAHUCharacteristics({
            type: "Success",
            data: response?.characteristics || [],
            [CURRENT_FETCH]: response?.newFetch,
            equipmentId: selectedItem?.id
          })
        )
      }
      fetchCharacteristics()
    }
  }, [dispatch, fetchAHUProperties, selectedItem])

  // Only show equipment linked to active devices in the building
  // Follows Chiller Performance Report pattern
  const equipments = useMemo<Equipment[]>(() => {
    // While devices are still loading or haven't been fetched, show all so the
    // list is not empty while waiting.
    if (loadingDevices || !devicesFetched) return allEquipments
    // activeDeviceIds is null only when deviceList hasn't been fetched (handled
    // above by !devicesFetched), so this is a safety guard.
    if (activeDeviceIds === null) return []
    // Empty Set means the building has no active automation devices Ã¢â‚¬â€
    // no equipment should be listed.
    if (!activeDeviceIds.size) return []
    return filterEquipmentByDeviceIds(allEquipments, activeDeviceIds)
  }, [allEquipments, activeDeviceIds, loadingDevices, devicesFetched])

  // Set initial selected equipment when coming from equipment summary
  useEffect(() => {
    if (
      selectedEquipment &&
      selectedEquipment?.type === selectedEquipmentType?.value
    ) {
      const preSelectedEquipment =
        equipments.find((item) => item?.id === selectedEquipment?.id) || {}
      dispatch(setSelectedEquipments(preSelectedEquipment))
    }
  }, [dispatch, selectedEquipment, selectedEquipmentType, equipments])

  useEffect(() => {
    let isMounted = true

    const translateEquipmentNames = async () => {
      if (!equipments?.length || selectedLanguage === "en") {
        if (isMounted) setTranslatedNamesById({})
        return
      }

      const newTranslatedNames = await bulkTranslate(
        equipments.reduce<Record<string, string>>((acc, equipment) => {
          acc[String(equipment.id)] = equipment.name
          return acc
        }, {})
      )

      if (!isMounted) return
      setTranslatedNamesById(newTranslatedNames)
    }

    translateEquipmentNames()

    return () => {
      isMounted = false
    }
  }, [equipments, selectedLanguage, bulkTranslate])

  const filteredEquipments = useMemo(() => {
    const getDisplayName = (equipment: Equipment) =>
      translatedNamesById[String(equipment.id)] || equipment.name

    let tempEquipments: Equipment[] = equipments
    if (searchText) {
      tempEquipments = equipments.filter((equipment) =>
        SearchRegex(getDisplayName(equipment), searchText)
      )
    }
    tempEquipments =
      sortByAlphanumericSpecialChars("name", "Asc", tempEquipments) || []
    return tempEquipments?.map((equipment) => ({
      label: getDisplayName(equipment),
      value: equipment.id
    }))
  }, [equipments, searchText, translatedNamesById])

  return (
    <div className={clsx("col-sm", styles.reportListWrapper)}>
      <div className={clsx(styles.reportPanel)}>
        <SecondaryNav withBorder>
          <LeftPanel loading={equipLoading}>
            <TranslateComponent>Select System or Equipment</TranslateComponent>
          </LeftPanel>
          <RightPanel loading={equipLoading}>
            <SearchInput
              onInputChange={handleSearch}
              onInputClear={() => setSearchText("")}
              searchText={searchText}
            />
          </RightPanel>
        </SecondaryNav>
        <div className={styles.panelList}>
          <div className={styles.panelListInner}>
            <div className={styles.reportListBody}>
              <RadioGroup
                radioGroupClassName={styles.radioButtonGroup}
                options={filteredEquipments}
                layout="column"
                handleChange={handleEquipmentChange}
                selected={selectedItem?.id}
                highLightText={searchText}
                translateHighlightLabel={false}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AHUEquipmentList
